"use client";

import { decodePath, hasHashTarget, scrollToSection, scrollToSectionWhenSettled } from "@v2/helpers/hashScroll";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

// Anything on the site can point at a section of a page - a menu entry, a chip in a
// page header, a banner an editor pinned to a landing page, a link written inside a
// text block - so the scroll is answered from one listener here rather than from each
// of the places a link can be written.
//
// Two things are answered: a link clicked on the page that holds the section, which
// the browser would otherwise jump to in one hop and under the site header; and an
// address that arrives with a hash on it, which the router does not act on at all.

/** A click the page has already answered, or one that was never ours to answer. */
function isPlainLeftClick(event: MouseEvent) {
    if (event.defaultPrevented) return false;

    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * The section a link points at, when that section is on the page being read. A link
 * that leads somewhere else is a navigation, and the page it lands on scrolls to the
 * hash itself.
 */
function sectionOnThisPage(anchor: HTMLAnchorElement): string | null {
    const href = anchor.getAttribute("href");
    if (!href) return null;

    let target: URL;
    try {
        target = new URL(href, window.location.href);
    } catch {
        return null;
    }

    if (target.origin !== window.location.origin) return null;
    if (!target.hash) return null;
    // Another address of the same page - one more filter, another sort - is a
    // navigation even though the path matches, and the list has to be fetched before
    // anything can be scrolled to.
    if (decodePath(target.pathname) !== decodePath(window.location.pathname)) return null;
    if (target.search !== window.location.search) return null;

    return hasHashTarget(target.hash) ? target.hash : null;
}

export default function HashLinkScroll() {
    // The address the router is on, which is what says a new page has arrived. Only
    // the path is read: useSearchParams() would opt every page that mounts this out of
    // server rendering, and a query never brings a hash of its own anyway.
    const pathname = usePathname();

    useEffect(() => {
        const onClick = (event: MouseEvent) => {
            if (!isPlainLeftClick(event)) return;

            const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
            // A link that opens in another tab or saves a file leaves this page alone.
            if (!anchor || (anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;

            const hash = sectionOnThisPage(anchor);
            if (!hash) return;

            event.preventDefault();
            // The address is left alone on purpose: a hash written into it would keep
            // the browser from repeating the jump the next time the same link is
            // clicked, and a section is not a page worth sharing on its own.
            scrollToSection(hash);
        };

        // Capture, so a link whose own handler takes the navigation over is still seen.
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, []);

    useEffect(() => {
        // Nothing writes a hash while a page is being read, so a hash in the address
        // always means a link that was followed to get here.
        const scrollToCurrentHash = () => (window.location.hash ? scrollToSectionWhenSettled(window.location.hash) : () => { });

        let cancel = scrollToCurrentHash();
        // A link to another section of the page being read, written by something that
        // sets the hash itself rather than going through the listener above.
        const onHashChange = () => {
            cancel();
            cancel = scrollToCurrentHash();
        };

        window.addEventListener("hashchange", onHashChange);
        return () => {
            cancel();
            window.removeEventListener("hashchange", onHashChange);
        };
    }, [pathname]);

    return null;
}
